import classNames from 'classnames';
import { type CSSProperties, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Marquee } from 'atoms/container/Marquee';
import { ScoreTarget } from 'atoms/icons/ScoreTarget';
import { Trophy } from 'atoms/icons/Trophy';
import { Accordion } from 'atoms/layout/Accordion';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyL, BodyM, BrandL, BrandM } from 'atoms/typography';
import { Img } from 'atoms/ui/Img';
import { TextureBackground } from 'atoms/ui/TextureBackground';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { laptopAndAbove } from 'style/mediaQuery';

import position1 from './assets/position1.webp';

const Root = styled.section`
  display: flex;
  flex-direction: column-reverse;
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    flex-direction: row;
    gap: calc(var(--unit) * 10);
    min-height: calc(var(--unit) * 94);
    padding: calc(var(--unit) * 10);
    position: relative;
  }
`;

const MarqueeWrapper = styled.div`
  mask-image: linear-gradient(to right, transparent, #000 10% 90%, transparent);
  @media ${laptopAndAbove} {
    width: 50%;
    height: var(--marquee-height);
    overflow: hidden;
  }
`;

const TextContainer = styled(Vertical).attrs({
  gap: 6,
})`
  justify-content: flex-start;
  min-height: calc(var(--unit) * 61);
`;

const TitleContainer = styled(Vertical).attrs({
  gap: 4,
})`
  z-index: 1;
`;
const AccordionTitle = styled(BrandM).attrs({
  bold: true,
  uppercase: true,
})`
  color: var(--c-nd-400-opaque);
  &.isOpen {
    color: var(--white);
  }
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
`;

const Title = styled(BrandL)`
  @media ${laptopAndAbove} {
    font: var(--t-brand-xxl);
  }
`;

export const OurCompetitions = () => {
  const [openAccordionId, setOpenAccordionId] = useState({
    leaderboards: true,
    'score-targets': false,
  });
  const isLaptopAndAbove = useIsLaptop();
  return (
    <Root>
      {!!isLaptopAndAbove && (
        <StyledTextureBackground
          background="wave1"
          mode="dark"
          color="var(--c-nd-400-opaque)"
        />
      )}
      <MarqueeWrapper
        style={
          {
            '--marquee-height': isLaptopAndAbove ? `${436}px` : `${356}px`,
          } as CSSProperties
        }
      >
        <Marquee>
          <Img
            src={position1}
            alt=""
            height={isLaptopAndAbove ? '436' : '356'}
            width={isLaptopAndAbove ? '376' : '215'}
          />
          <Img
            src={position1}
            alt=""
            height={isLaptopAndAbove ? '436' : '356'}
            width={isLaptopAndAbove ? '376' : '215'}
          />
          <Img
            src={position1}
            alt=""
            height={isLaptopAndAbove ? '436' : '356'}
            width={isLaptopAndAbove ? '376' : '215'}
          />
        </Marquee>
      </MarqueeWrapper>
      <TextContainer>
        <TitleContainer gap={4}>
          <Vertical gap={2}>
            <BrandM uppercase color="var(--c-limited)">
              <FormattedMessage
                id="HowToPlay.OurCompetitions.eyebrow"
                defaultMessage="Ways to Play"
              />
            </BrandM>
            <Title>
              <FormattedMessage
                id="HowToPlay.OurCompetitions.title"
                defaultMessage="Our Competitions"
              />
            </Title>
          </Vertical>
          <BodyL>
            <FormattedMessage
              id="HowToPlay.OurCompetitions.description"
              defaultMessage="When your players perform well in their actual real-life games,
your team earns points and brings you closer to better rewards."
            />
          </BodyL>
        </TitleContainer>
        <Accordion
          startsOpen
          onExpandChange={expand =>
            setOpenAccordionId(prev => ({ ...prev, leaderboards: expand }))
          }
          title={
            <Horizontal>
              <Trophy color="var(--c-blue-300)" />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId.leaderboards,
                })}
              >
                <FormattedMessage
                  id="HowToPlay.OurCompetitions.leaderboards"
                  defaultMessage="Leaderboards"
                />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <BodyM>
            <FormattedMessage
              id="HowToPlay.OurCompetitions.leaderboards.description"
              defaultMessage="Compete against other managers for major rewards as you challenge
for top places."
            />
          </BodyM>
        </Accordion>
        <Accordion
          onExpandChange={expand =>
            setOpenAccordionId(prev => ({ ...prev, 'score-targets': expand }))
          }
          title={
            <Horizontal>
              <ScoreTarget color="var(--c-blue-300)" />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId['score-targets'],
                })}
              >
                <FormattedMessage
                  id="HowToPlay.OurCompetitions.scoreTargets"
                  defaultMessage="Score Targets"
                />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <BodyM>
            <FormattedMessage
              id="HowToPlay.OurCompetitions.scoreTargets.description"
              defaultMessage="Hit specific point thresholds to earn guaranteed cash or card rewards."
            />
          </BodyM>
        </Accordion>
      </TextContainer>
    </Root>
  );
};
